import { Controller } from '@hotwired/stimulus'
import { computePosition, offset, flip, shift } from '@floating-ui/dom'

export default class extends Controller {
  static targets = ['button', 'popover', 'hiddenField', 'display', 'searchInput', 'option', 'emptyMessage', 'optionsList']
  static values = { searchable: Boolean }

  connect () {
    this.activeIndex = -1
  }

  reposition () {
    computePosition(this.buttonTarget, this.popoverTarget, {
      placement: 'bottom-start',
      middleware: [offset(8), flip(), shift({ padding: 8 })]
    }).then(({ x, y }) => {
      Object.assign(this.popoverTarget.style, { left: `${x}px`, top: `${y}px` })
    })
  }

  selectItem (event) {
    const item = event.currentTarget
    const value = item.dataset.value
    const template = item.querySelector('template')

    this.hiddenFieldTarget.value = value
    if (template) this.displayTarget.innerHTML = template.innerHTML

    this.hiddenFieldTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.popoverTarget.hidePopover()
  }

  onToggle (event) {
    const isOpen = event.newState === 'open'
    this.buttonTarget.setAttribute('aria-expanded', isOpen ? 'true' : 'false')

    if (isOpen) {
      this.reposition()
      if (this.searchableValue) {
        this.resetFilter()
        this.activeIndex = -1
        requestAnimationFrame(() => this.searchInputTarget.focus())
      }
    }
  }

  filter () {
    const query = this.searchInputTarget.value.trim().toLowerCase()
    let firstVisible = -1
    this.optionTargets.forEach((opt, i) => {
      const matches = query === '' || opt.dataset.searchText.includes(query)
      opt.classList.toggle('hidden', !matches)
      if (matches && firstVisible === -1) firstVisible = i
    })
    this.hasEmptyMessageTarget && this.emptyMessageTarget.classList.toggle('hidden', firstVisible !== -1)
    this.setActiveIndex(firstVisible)
  }

  onKey (event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      this.moveActive(1)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      this.moveActive(-1)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const active = this.optionTargets[this.activeIndex]
      if (active && !active.classList.contains('hidden')) active.click()
    } else if (event.key === 'Escape') {
      this.popoverTarget.hidePopover()
    }
  }

  moveActive (delta) {
    const visible = this.optionTargets
      .map((opt, i) => opt.classList.contains('hidden') ? -1 : i)
      .filter(i => i !== -1)
    if (visible.length === 0) return

    let currentPos = visible.indexOf(this.activeIndex)
    if (currentPos === -1) currentPos = delta > 0 ? -1 : visible.length
    const nextPos = (currentPos + delta + visible.length) % visible.length
    this.setActiveIndex(visible[nextPos])

    const active = this.optionTargets[this.activeIndex]
    if (active) active.scrollIntoView({ block: 'nearest' })
  }

  setActiveIndex (index) {
    this.optionTargets.forEach((opt, i) => {
      opt.setAttribute('aria-selected', i === index ? 'true' : 'false')
    })
    this.activeIndex = index
    if (index >= 0) {
      this.buttonTarget.setAttribute('aria-activedescendant', this.optionTargets[index].id)
    } else {
      this.buttonTarget.removeAttribute('aria-activedescendant')
    }
  }

  resetFilter () {
    if (!this.hasSearchInputTarget) return
    this.searchInputTarget.value = ''
    this.optionTargets.forEach(opt => {
      opt.classList.remove('hidden')
      opt.setAttribute('aria-selected', 'false')
    })
    this.buttonTarget.removeAttribute('aria-activedescendant')
    if (this.hasEmptyMessageTarget) this.emptyMessageTarget.classList.add('hidden')
  }
}
